import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { ISecureStorage } from "../../classes/interfaces/isecurestorage";
import { withSpinner } from "../../extensions/withSpinner.extension";
import { AppFeature } from "../../types/appfeature";
import { API_ROOT, SECURE_LOCAL_STORAGE } from "../../types/constants/appconstants";
import { Market } from "../../types/organization";

@Injectable({
    "providedIn": "root"
})
export class OrganizationService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private storage = inject<ISecureStorage>(SECURE_LOCAL_STORAGE);
    notifyMarketsChanged = new BehaviorSubject<any>(null);
    private orgServiceBaseUrl = this.apiRoot + "api/organizations";    

    private storageKey: string = "Markets";

    getAllMarkets(): Observable<Market[]> {
        var cacheOrgs = this.storage.getItem<Market>(this.storageKey) as Market[];
        if(cacheOrgs) {
            const orgs$ = of(cacheOrgs);
            return orgs$;
        }

        const result$ = withSpinner(this.http, "Loading Markets...").get<Market[]>(this.orgServiceBaseUrl + "/markets").pipe(
            tap((mkts: Market[]) => this.storage.setItem(this.storageKey, mkts))
        );

        return result$;
    }

    getActiveMarkets(): Observable<Market[]> {
        return this.getAllMarkets().pipe(
            map(mkts => mkts.filter(mkt => mkt.constructionStatusId === 1))
        );
    }

    getAppFeatures(): Observable<AppFeature[]> {
        // GET /appfeatures
        return withSpinner(this.http).get<AppFeature[]>(`${this.orgServiceBaseUrl}/appfeatures`);
    }

    // READ: This method is actually getting the market by the sourceKey rather than the orgId
    getMarketById(marketId: number): Observable<Market> {
        return this.getAllMarkets().pipe(
            map(mkts => mkts.find(mkt => mkt.sourceKey === marketId.toString()))
        );
    }

    getMarketByNumber(marketNumber: string): Observable<Market> {
        const market = withSpinner(this.http).get<Market>(`${this.orgServiceBaseUrl}/markets/${marketNumber}`)
        return market;
    }

    saveMarket(market: Market): Observable<Market> {
        return withSpinner(this.http).patch<Market>(`${this.orgServiceBaseUrl}/market`, { "market": market }).pipe(
            tap(() => this.refreshMarkets())
        );
    }

    private refreshMarkets () {
        this.storage.removeItem(this.storageKey);
        this.notifyMarketsChanged.next(null);
    }
}
